/**
 * ApiClientContext — exposes one shared `ApiClient` instance to the tree.
 * The client gets the bearer token via `getMsalToken` (set up in
 * `services/apiClient.ts`); it does not depend on AuthContext, so it's
 * safe to mount above or below the AuthProvider.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { ApiClient } from '@retail-ops/api-client';
import {
  createApiClient,
  apiClientFactoryOptionsFromEnv,
} from '@/services/apiClient';

const ApiClientContext = createContext<ApiClient | null>(null);

export function ApiClientProvider({ children }: { children: ReactNode }): JSX.Element {
  const client = useMemo(() => createApiClient(apiClientFactoryOptionsFromEnv()), []);
  return (
    <ApiClientContext.Provider value={client}>{children}</ApiClientContext.Provider>
  );
}

export function useApiClient(): ApiClient {
  const ctx = useContext(ApiClientContext);
  if (!ctx) throw new Error('useApiClient must be used within ApiClientProvider');
  return ctx;
}
